// Service for managing Payment Integrity filter state in sessionStorage
// Single unified key 'piFilterState' stores all PI filter selections

import { PIFilterState } from '../types/piFilters.types';
import { getPIDefaultFilters } from '../constants/piFilterConfig';
import { PI_LOB_OPTIONS } from '../constants/piFilterOptions';
import {
  getReimbursementLobCompatibilityValue,
  getReimbursementLobSelectionFromState,
  normalizeReimbursementLobSelection,
} from '@src/apps/Reimbursement/utils/reimbursementLobSelection';

/**
 * Single sessionStorage key for all PI filter state
 */
export const PI_FILTER_STATE_KEY = 'piFilterState';

/**
 * Get PI filters from the unified sessionStorage key
 */
// Migrate old LOB display-name values (stored before the code-based fix) to codes
const LOB_DISPLAY_NAME_TO_CODE: Record<string, string> = {
  'EI': 'E&I',
  'CS': 'C&S',
  'MR': 'M&R',
  'EXC': 'Individual exchange',
  'Commercial': 'E&I',
  'Medicaid': 'C&S',
  'Medicare': 'M&R',
  'Individual Exchange': 'Individual exchange',
  'individual exchange': 'Individual exchange',
};

const PI_LOB_VALUES = PI_LOB_OPTIONS.filter((option) => option.value !== 'All').map(
  (option) => option.value
);

const OVERVIEW_TO_PI_LOB: Record<string, string> = {
  EI: 'E&I',
  CS: 'C&S',
  MR: 'M&R',
  EXC: 'Individual exchange',
};

const getOverviewLobSelection = (): string[] | null => {
  try {
    const stateStr = sessionStorage.getItem('state');
    if (!stateStr) return null;

    const state = JSON.parse(stateStr) as Record<string, unknown>;
    const hasMultiSelect = Object.prototype.hasOwnProperty.call(state, 'lineOfBusinessMultiSelect');
    const hasLegacySelection = typeof state.lineOfBusiness === 'string' &&
      state.lineOfBusiness.toUpperCase() !== 'ALL';
    if (!hasMultiSelect && !hasLegacySelection) {
      return null;
    }

    const overviewSelection = getReimbursementLobSelectionFromState(state);
    return overviewSelection
      .map((code) => OVERVIEW_TO_PI_LOB[code.toUpperCase()])
      .filter((value): value is string => Boolean(value));
  } catch {
    return null;
  }
};

export const getPIFiltersFromSession = (): PIFilterState => {
  try {
    const defaults = getPIDefaultFilters();
    const overviewSelection = getOverviewLobSelection();
    const stateStr = sessionStorage.getItem(PI_FILTER_STATE_KEY);
    if (!stateStr) {
      if (overviewSelection === null) return defaults;
      return {
        ...defaults,
        lineOfBusiness: getReimbursementLobCompatibilityValue(overviewSelection, PI_LOB_VALUES),
        lineOfBusinessMultiSelect: overviewSelection,
      };
    }

    const stored = JSON.parse(stateStr);
    const migratedState = {
      ...stored,
      lineOfBusiness: typeof stored.lineOfBusiness === 'string'
        ? stored.lineOfBusiness.split(',').map((value: string) => LOB_DISPLAY_NAME_TO_CODE[value.trim()] ?? value)
        : stored.lineOfBusiness,
    };
    const lineOfBusinessMultiSelect = overviewSelection ??
      getReimbursementLobSelectionFromState(migratedState, PI_LOB_VALUES);
    const lineOfBusiness = getReimbursementLobCompatibilityValue(lineOfBusinessMultiSelect, PI_LOB_VALUES);
    return {
      timePeriod: stored.timePeriod || defaults.timePeriod,
      taxId: stored.taxId || defaults.taxId,
      contractIds: Array.isArray(stored.contractIds) ? stored.contractIds : defaults.contractIds,
      lineOfBusiness,
      lineOfBusinessMultiSelect,
      claimType: stored.claimType || defaults.claimType,
      serviceType: stored.serviceType || defaults.serviceType,
      vendor: stored.vendor || defaults.vendor,
    };
  } catch (error) {
    return getPIDefaultFilters();
  }
};

/**
 * Save PI filters to the unified sessionStorage key.
 * Also writes backward-compatible keys for external consumers
 * (piTimePeriod, reactState) that haven't migrated yet.
 */
export const savePIFiltersToSession = (filters: PIFilterState, changedKeys?: string[]): void => {
  try {
    const lineOfBusinessMultiSelect = normalizeReimbursementLobSelection(
      filters.lineOfBusinessMultiSelect || filters.lineOfBusiness,
      PI_LOB_VALUES
    );
    // Write unified key
    sessionStorage.setItem(PI_FILTER_STATE_KEY, JSON.stringify({
      timePeriod: filters.timePeriod,
      taxId: filters.taxId,
      contractIds: filters.contractIds,
      lineOfBusiness: getReimbursementLobCompatibilityValue(lineOfBusinessMultiSelect, PI_LOB_VALUES),
      lineOfBusinessMultiSelect,
      claimType: filters.claimType,
      serviceType: filters.serviceType,
      vendor: filters.vendor,
    }));

    // Backward-compat: piTimePeriod for external consumers (EvaluationCard, Table, etc.)
    if (filters.timePeriod) {
      sessionStorage.setItem('piTimePeriod', JSON.stringify({
        value: filters.timePeriod.value || '',
        name: filters.timePeriod.title || filters.timePeriod.value || '',
      }));
    }

    // Backward-compat: reactState for external consumers (commonUtilityServices, etc.)
    sessionStorage.setItem('reactState', JSON.stringify({
      claimType: filters.claimType,
      serviceType: filters.serviceType,
      vendor: filters.vendor,
    }));

    // Backward-compat: sync taxId to 'state' session key for CP (ReimbursementFilters-connect)
    // and journeyExtendedTinFilterSelected for Overview/other journeys
    // CP reads state.taxId as [{ Tin, Tinname }]
    try {
      const stateStr = sessionStorage.getItem('state');
      const state = stateStr ? JSON.parse(stateStr) : {};

      // Only sync taxId to state when it was explicitly changed by the caller.
      // When changedKeys is provided (partial update from updatePIFiltersInSession),
      // skip taxId sync unless 'taxId' is in the list. When changedKeys is not
      // provided (full state write from Redux), always sync.
      const shouldSyncTaxId = !changedKeys || changedKeys.includes('taxId');
      if (shouldSyncTaxId) {
        const mappedTaxIds = (filters.taxId || []).map((t: any) => ({ Tin: t.Tin, Tinname: t.TinName || t.Tin }));
        state.taxId = mappedTaxIds;

        // Sync journeyExtendedTinFilterSelected so Overview and other journeys pick up PI TIN selections
        if (!state.journeyExtendedTinFilterSelected) {
          state.journeyExtendedTinFilterSelected = {};
        }
        state.journeyExtendedTinFilterSelected.taxId = mappedTaxIds;
      }

      // Only sync claimsFilter when it was explicitly changed.
      const shouldSyncClaimType = !changedKeys || changedKeys.includes('claimType');
      if (shouldSyncClaimType) {
        const piClaim = (filters.claimType || 'All').toString();
        if (piClaim === 'Professional') {
          state.claimsFilter = 'PROFESSIONAL';
        } else if (piClaim === 'Facility') {
          state.claimsFilter = 'INSTITUTIONAL';
        } else {
          // Reset to 'All' — clear claimsFilter so other journeys (CP) see no filter
          delete state.claimsFilter;
        }
      }

      sessionStorage.setItem('state', JSON.stringify(state));
    } catch (e) {
      // ignore state sync errors
    }

    // Backward-compat: piServiceType for returnParamsValuesV2
    if (filters.serviceType && filters.serviceType !== 'All') {
      sessionStorage.setItem('piServiceType', JSON.stringify({ name: filters.serviceType }));
    } else {
      sessionStorage.removeItem('piServiceType');
    }

    // Backward-compat: piVendors for returnParamsValuesV2
    if (filters.vendor && filters.vendor !== 'All') {
      sessionStorage.setItem('piVendors', JSON.stringify([{ name: filters.vendor }]));
    } else {
      sessionStorage.removeItem('piVendors');
    }
  } catch (error) {
    // silent
  }
};

/**
 * Update specific fields in the unified PI filter state without overwriting others
 */
export const updatePIFiltersInSession = (partial: Partial<PIFilterState>): void => {
  try {
    const current = getPIFiltersFromSession();
    const updated = { ...current, ...partial };
    savePIFiltersToSession(updated, Object.keys(partial));
  } catch (error) {
    // silent
  }
};

/**
 * Keep only selected vendors that exist in the available vendor list.
 * Returns 'All' when nothing is selected or no selected vendor remains.
 */
export const pruneVendorSelection = (
  vendor: string | undefined,
  availableVendors: Array<{ value: string }>
): string => {
  if (!vendor || vendor === 'All') return 'All';
  const available = new Set((availableVendors || []).map((v) => v.value));
  const kept = vendor
    .split(',')
    .map((v) => v.trim())
    .filter((v) => !!v && available.has(v));
  return kept.length > 0 ? kept.join(',') : 'All';
};

/**
 * Clear all PI filter state from sessionStorage
 */
export const clearPIFiltersFromSession = (): void => {
  try {
    sessionStorage.removeItem(PI_FILTER_STATE_KEY);
    sessionStorage.removeItem('piTimePeriod');
    sessionStorage.removeItem('reactState');
    sessionStorage.removeItem('piServiceType');
    sessionStorage.removeItem('piVendors');
  } catch (error) {
    // silent
  }
};

